import { Textarea } from "../ui/textarea";
import { LogoUploader } from "./LogoUploader";

interface AdminHomeTableProps {
  form: any;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
}

function BrandingRows({ form, handleChange }: AdminHomeTableProps) {
  return (
    <>
      <tr className="bg-muted/10 font-bold text-xs text-brand-primary uppercase tracking-wider">
        <td colSpan={3} className="px-4 py-2">Branding & Biography</td>
      </tr>
      <tr className="hover:bg-muted/5 transition-colors">
        <td className="px-4 py-3 font-medium border-r border-border/40 align-middle">Punchline</td>
        <td className="px-3 py-2 border-r border-border/40">
          <Textarea
            id="punchline_en"
            name="punchline_en"
            rows={2}
            value={form.punchline_en || ""}
            onChange={handleChange}
            className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary min-h-[48px] resize-y py-1.5 text-xs leading-normal"
            placeholder={"e.g. Architect of AI Systems\nMentor of Next Gen"}
          />
        </td>
        <td className="px-3 py-2">
          <Textarea
            id="punchline_tr"
            name="punchline_tr"
            rows={2}
            value={form.punchline_tr || ""}
            onChange={handleChange}
            className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary min-h-[48px] resize-y py-1.5 text-xs leading-normal"
            placeholder={"örn. Yapay Zeka Sistemleri Mimarı\nGelecek Nesil Mentoru"}
          />
        </td>
      </tr>
      <tr className="hover:bg-muted/5 transition-colors">
        <td className="px-4 py-3 font-medium border-r border-border/40 align-middle">Summary / Biography</td>
        <td className="px-3 py-2 border-r border-border/40">
          <Textarea
            id="bio_en"
            name="bio_en"
            rows={4}
            value={form.bio_en || ""}
            onChange={handleChange}
            className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary text-xs resize-y"
            placeholder="English summary..."
          />
        </td>
        <td className="px-3 py-2">
          <Textarea
            id="bio_tr"
            name="bio_tr"
            rows={4}
            value={form.bio_tr || ""}
            onChange={handleChange}
            className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary text-xs resize-y"
            placeholder="Türkçe özet..."
          />
        </td>
      </tr>
      <tr className="hover:bg-muted/5 transition-colors border-t border-border/40">
        <td className="px-4 py-3 font-medium border-r border-border/40 align-middle">Profile Picture</td>
        <td colSpan={2} className="px-3 py-3">
          <LogoUploader
            label="Upload Profile Image"
            logoUrl={form.profile_image}
            onChange={(base64) => handleChange({ target: { name: "profile_image", value: base64 } } as any)}
            onRemove={() => handleChange({ target: { name: "profile_image", value: "" } } as any)}
          />
        </td>
      </tr>
    </>
  );
}

function HighlightRow({ title, prefix, form, handleChange }: { title: string; prefix: string; form: any; handleChange: any }) {
  const c_en = `${prefix}_c_en`;
  const c_tr = `${prefix}_c_tr`;
  const d_en = `${prefix}_d_en`;
  const d_tr = `${prefix}_d_tr`;

  return (
    <>
      <tr className="hover:bg-muted/5 transition-colors border-t border-border/40">
        <td className="px-4 py-3 font-medium border-r border-border/40 align-middle">{title} Title Override</td>
        <td className="px-3 py-2 border-r border-border/40">
          <Textarea
            id={c_en}
            name={c_en}
            value={form[c_en] || ""}
            onChange={handleChange}
            className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary h-9 min-h-[36px] resize-y py-1.5 text-xs"
            placeholder="Title override (EN)"
          />
        </td>
        <td className="px-3 py-2">
          <Textarea
            id={c_tr}
            name={c_tr}
            value={form[c_tr] || ""}
            onChange={handleChange}
            className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary h-9 min-h-[36px] resize-y py-1.5 text-xs"
            placeholder="Title override (TR)"
          />
        </td>
      </tr>
      <tr className="hover:bg-muted/5 transition-colors">
        <td className="px-4 py-3 font-medium border-r border-border/40 align-middle">{title} Subtext Override</td>
        <td className="px-3 py-2 border-r border-border/40">
          <Textarea
            id={d_en}
            name={d_en}
            value={form[d_en] || ""}
            onChange={handleChange}
            className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary h-9 min-h-[36px] resize-y py-1.5 text-xs"
            placeholder="Subtext/Duration (EN)"
          />
        </td>
        <td className="px-3 py-2">
          <Textarea
            id={d_tr}
            name={d_tr}
            value={form[d_tr] || ""}
            onChange={handleChange}
            className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary h-9 min-h-[36px] resize-y py-1.5 text-xs"
            placeholder="Subtext/Duration (TR)"
          />
        </td>
      </tr>
    </>
  );
}

export function AdminHomeTable({ form, handleChange }: AdminHomeTableProps) {
  return (
    <div className="overflow-x-auto border border-border/60 rounded-xl bg-card/30">
      <table className="min-w-full divide-y divide-border/40 text-sm">
        <thead className="bg-muted/40 font-semibold text-brand-secondary">
          <tr>
            <th className="px-4 py-2.5 text-left w-1/4 border-r border-border/40 text-xs uppercase tracking-wider">Field</th>
            <th className="px-4 py-2.5 text-left w-3/8 border-r border-border/40 text-xs uppercase tracking-wider">English (EN)</th>
            <th className="px-4 py-2.5 text-left w-3/8 text-xs uppercase tracking-wider">Turkish (TR)</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/40 text-left">
          <BrandingRows form={form} handleChange={handleChange} />

          <tr className="bg-muted/10 font-bold text-xs text-brand-primary uppercase tracking-wider border-t border-border/40">
            <td colSpan={3} className="px-4 py-2">Highlights Overrides (Milestone Customizations)</td>
          </tr>

          <HighlightRow title="Career" prefix="job" form={form} handleChange={handleChange} />
          <HighlightRow title="Academic" prefix="acad" form={form} handleChange={handleChange} />
          <HighlightRow title="Hobbies" prefix="hobby" form={form} handleChange={handleChange} />
          <HighlightRow title="Volunteering" prefix="comm" form={form} handleChange={handleChange} />

          <tr className="bg-muted/10 font-bold text-xs text-brand-primary uppercase tracking-wider border-t border-border/40">
            <td colSpan={3} className="px-4 py-2">Operational Configuration (Availability & Timezone)</td>
          </tr>

          <tr className="hover:bg-muted/5 transition-colors">
            <td className="px-4 py-3 font-medium border-r border-border/40 align-middle">Contract Availability</td>
            <td colSpan={2} className="px-3 py-2">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="accepting_contracts"
                  name="accepting_contracts"
                  checked={form.accepting_contracts || false}
                  onChange={handleChange}
                  className="w-4 h-4 rounded text-brand-primary border-border focus:ring-brand-primary bg-background/50 cursor-pointer"
                />
                <label htmlFor="accepting_contracts" className="text-xs font-semibold cursor-pointer text-foreground">
                  {form.accepting_contracts ? "Green: Accepting new client contracts" : "Orange: Fully booked but open to chat"}
                </label>
              </div>
            </td>
          </tr>

          <tr className="hover:bg-muted/5 transition-colors border-t border-border/40">
            <td className="px-4 py-3 font-medium border-r border-border/40 align-middle">Local Timezone Offset (GMT)</td>
            <td colSpan={2} className="px-3 py-2">
              <select
                id="timezone_offset"
                name="timezone_offset"
                value={form.timezone_offset || "3"}
                onChange={handleChange}
                className="bg-background/50 border border-border/40 rounded px-2.5 py-1 text-xs text-foreground focus-visible:ring-1 focus-visible:ring-brand-primary outline-none"
              >
                <option value="-12">GMT-12</option>
                <option value="-11">GMT-11</option>
                <option value="-10">GMT-10</option>
                <option value="-9">GMT-9</option>
                <option value="-8">GMT-8 (PST)</option>
                <option value="-7">GMT-7 (MST)</option>
                <option value="-6">GMT-6 (CST)</option>
                <option value="-5">GMT-5 (EST)</option>
                <option value="-4">GMT-4</option>
                <option value="-3">GMT-3</option>
                <option value="-2">GMT-2</option>
                <option value="-1">GMT-1</option>
                <option value="0">GMT+0 (UTC / WET)</option>
                <option value="1">GMT+1 (CET)</option>
                <option value="2">GMT+2 (EET)</option>
                <option value="3">GMT+3 (Istanbul / MSK)</option>
                <option value="4">GMT+4</option>
                <option value="5">GMT+5</option>
                <option value="6">GMT+6</option>
                <option value="7">GMT+7</option>
                <option value="8">GMT+8 (SGT / HKT)</option>
                <option value="9">GMT+9 (JST)</option>
                <option value="10">GMT+10 (AEST)</option>
                <option value="11">GMT+11</option>
                <option value="12">GMT+12</option>
              </select>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
